<script setup lang="ts">
import { computed } from 'vue';
import { useVideoStore } from '../stores/videoStore';

const videoStore = useVideoStore();

const totalPages = computed(() => videoStore.totalPages);
const currentPage = computed(() => videoStore.currentPage);

function goPage(n: number) {
  videoStore.goPage(n);
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

const pageRange = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const isMobile = window.innerWidth < 768;
  const range = isMobile ? 1 : 3;
  let start = Math.max(1, current - range);
  let end = Math.min(total, start + range * 2);
  if (end - start < range * 2) start = Math.max(1, end - range * 2);
  return { start, end };
});
</script>

<template>
  <div v-if="totalPages > 1" class="pagination">
    <button
      class="page-btn"
      :disabled="currentPage === 1"
      @click="goPage(currentPage - 1)"
    >上一页</button>

    <button v-if="pageRange.start > 1" class="page-btn" @click="goPage(1)">1</button>
    <span v-if="pageRange.start > 2" class="page-info">…</span>

    <button
      v-for="p in Array.from({ length: pageRange.end - pageRange.start + 1 }, (_, i) => pageRange.start + i)"
      :key="p"
      class="page-btn"
      :class="{ active: p === currentPage }"
      @click="goPage(p)"
    >{{ p }}</button>

    <span v-if="pageRange.end < totalPages - 1" class="page-info">…</span>
    <button v-if="pageRange.end < totalPages" class="page-btn" @click="goPage(totalPages)">{{ totalPages }}</button>

    <button
      class="page-btn"
      :disabled="currentPage === totalPages"
      @click="goPage(currentPage + 1)"
    >下一页</button>

    <span class="page-info">第 {{ currentPage }}/{{ totalPages }} 页</span>
  </div>
</template>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
  margin-bottom: 20px;
  width: 100%;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 0 10px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
}
.page-btn:hover:not(:disabled):not(.active) {
  border-color: var(--accent);
  color: var(--accent);
}
.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.page-btn.active {
  background: var(--accent-gradient);
  border-color: transparent;
  color: white;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.page-info {
  color: var(--text-muted);
  font-size: 13px;
  margin: 0 8px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .pagination {
    flex-wrap: wrap;
    padding: 0 16px;
    gap: 4px;
    margin-top: 16px;
    max-width: 100%;
  }

  .page-btn {
    min-width: 32px;
    height: 32px;
    font-size: 12px;
    padding: 0 8px;
  }

  .page-info {
    font-size: 12px;
    margin: 0 4px;
    width: 100%;
    text-align: center;
    order: 99;
    margin-top: 4px;
  }
}
</style>
